import { SectionCard } from '@/components/SectionCard/SectionCard'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { UserAccount } from '@/features/AccountManagement/components/UserAccount'
import {
  type GetUsersByEmailError,
  type GetUsersByEmailResponse,
  useGetUsersByEmail,
} from '@/generated/http-clients/v2ApiComponents'
import { applicationRootRoute } from '@/router/rootRoutes'
import {
  Grid,
  GridItem,
  Icon,
  Input,
  InputGroup,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { type UseNavigateResult, createRoute } from '@tanstack/react-router'
import {
  LucideMailCheck,
  LucideMailWarning,
  LucideUserCheck,
  Search,
} from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const PATH = 'accounts'

export const accountManagementRoute = createRoute({
  getParentRoute: () => applicationRootRoute,
  path: PATH,
  component: () => <AccountManagement />,
  validateSearch: z.object({
    email: z.string().email().optional(),
  }),
})

export function routeToAccountManagementWithPrefilledEmail(
  navigate: UseNavigateResult<string>,
  email: string,
) {
  navigate({
    to: `/${PATH}`,
    search: {
      email,
    },
  })
}

// NOTE: Exported only for testing purposes, DO NOT import this component directly.
export function AccountManagement() {
  const { t } = useTranslation('features', { useSuspense: false })
  const { email: searchParamEmail } = accountManagementRoute.useSearch()
  const [email, setEmail] = React.useState(searchParamEmail || '')

  const emailSchema = React.useMemo(
    () =>
      z
        .string()
        .min(1, 'Required')
        .email({
          message: String(
            t('accountManagement.search.validation.invalidEmail'),
          ),
        }),
    [t],
  )

  const { success: isEmailValid } = emailSchema.safeParse(email)

  const userQuery = useGetUsersByEmail(
    { queryParams: { email } },
    { enabled: isEmailValid },
  )

  const isUserFound =
    userQuery.isSuccess && userQuery.data?.length
      ? userQuery.data?.length > 0
      : false

  return (
    <PageLayout title={t('accountManagement.pageTitle')} maxWidth="8xl">
      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={4}>
        <GridItem>
          <SectionCard title={t('accountManagement.search.title')}>
            <VStack alignItems="start" gap={4} width="100%">
              <InputGroup
                startElement={<Icon as={Search} size="md" />}
                startElementProps={{ pointerEvents: 'none' }}
                endElement={
                  <SearchInputIcon
                    isUserFound={isUserFound}
                    isEmailValid={isEmailValid}
                    isEmailNotEmpty={email.length > 0}
                  />
                }
              >
                <Input
                  placeholder={String(
                    t('accountManagement.search.placeholder'),
                  )}
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  name="email"
                  type="text"
                />
              </InputGroup>
            </VStack>
          </SectionCard>
        </GridItem>
      </Grid>
      <PageContent
        userQuery={userQuery}
        isUserFound={isUserFound}
        email={email}
        isEmailValid={isEmailValid}
      />
    </PageLayout>
  )
}

function PageContent({
  userQuery,
  isEmailValid,
  email,
  isUserFound,
}: Readonly<{
  userQuery: UseQueryResult<GetUsersByEmailResponse, GetUsersByEmailError>
  email: string
  isEmailValid: boolean
  isUserFound: boolean
}>) {
  const { t } = useTranslation('features', { useSuspense: false })

  if (userQuery.isError) {
    return (
      <SectionCard title={t('accountManagement.error.title')}>
        <Text mt={2} color="red.600">
          {t('accountManagement.error.description')}
        </Text>
      </SectionCard>
    )
  }

  if (userQuery.isLoading) {
    return <SkeletonLoader />
  }

  if (userQuery.isSuccess && !userQuery.data?.length && isEmailValid) {
    return (
      <SectionCard title={t('accountManagement.error.title')}>
        <Text>{t('accountManagement.noResults', { email })}</Text>
      </SectionCard>
    )
  }

  if (isUserFound) {
    return userQuery?.data?.map((user) => (
      <UserAccount user={user} email={email} key={user.id} />
    ))
  }
}

function SearchInputIcon({
  isEmailNotEmpty,
  isEmailValid,
  isUserFound,
}: Readonly<{
  isUserFound: boolean
  isEmailValid: boolean
  isEmailNotEmpty: boolean
}>) {
  if (isUserFound) {
    return <Icon as={LucideUserCheck} size="md" color="green.600" />
  }

  if (isEmailValid) {
    return <Icon as={LucideMailCheck} size="md" color="green.600" />
  }

  if (isEmailNotEmpty) {
    return <Icon as={LucideMailWarning} size="md" color="orange.600" />
  }

  return null
}

function SkeletonLoader() {
  return (
    <SectionCard>
      <Skeleton h={8} w={60} mt={1} mb={4} />
      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={8}>
        <GridItem>
          <SectionCard width="100%" height="100%" p={0}>
            <Skeleton h={6} w={40} mb={10} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} />
          </SectionCard>
        </GridItem>
        <GridItem>
          <SectionCard width="100%" height="100%" p={0}>
            <Skeleton h={6} w={40} mb={10} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} mb={5} />
            <Skeleton h={6} />
          </SectionCard>
        </GridItem>
      </Grid>
    </SectionCard>
  )
}
